import React, { useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { joinParty, isValidPartyCode, ensureJoinParty } from "../firebaseClient";
import { KNOWN_JOBS } from "../constants/battle/jobs";
import { fetchLoaSummary } from "../lib/loaClient";
import AppModal from "./AppModal";

export default function JoinPartyModal({ onClose, onJoined }) {
  const { user: authUser } = useAuth();

  const [partyCode, setPartyCode] = useState("");
  const [nickname, setNickname] = useState("");
  const [jobId, setJobId] = useState("");           // 서버로 보낼 id
  const [jobLabel, setJobLabel] = useState("");     // 표시용(클래스명)
  const [engraving, setEngraving] = useState("");   // 표시/서버 전송
  const [combatPower, setCombatPower] = useState(null); // hidden
  const [level, setLevel] = useState(null); // hidden
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const codeRef = useRef(null); // 초기 포커스

  // 클래스명 → 네 프로젝트의 jobId로 매핑
  const mapClassToJobId = (className) => {
    const norm = (s) => String(s || "").replace(/\s/g, "").toLowerCase();
    const t = norm(className);
    for (const j of (KNOWN_JOBS || [])) {
      const id = typeof j === "string" ? j : (j.id || j.key || j.code || j.name || j.label);
      const label = typeof j === "string" ? j : (j.label || j.name || id);
      if (norm(id) === t || norm(label) === t) return String(id);
    }
    return String(className); // 못 찾으면 원문 사용
  };

  function normalizeCode(v) {
    const allowed = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    return String(v || "")
      .toUpperCase()
      .split("")
      .filter((ch) => allowed.includes(ch))
      .slice(0, 6)
      .join("");
  }
  const handleCodeChange = (e) => setPartyCode(normalizeCode(e.target.value));

  const inputCls =
    "rounded-lg border border-zinc-950/10 bg-white px-4 py-2 text-sm font-semibold " +
    "text-zinc-950 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 " +
    "dark:bg-zinc-900 dark:text-zinc-100 dark:border-white/10 dark:placeholder-zinc-500 dark:focus:ring-indigo-400";

  const valid =
    authUser &&
    isValidPartyCode?.(partyCode) &&
    nickname.trim().length > 0 &&
    nickname.trim().length <= 12 &&
    jobId && engraving; // 닉네임 확인으로 채워져야 활성화

  async function checkNickname() {
    const name = nickname.trim();
    if (!name) { setErr("닉네임을 입력하세요."); return; }
    setErr(""); setChecking(true);

    try {
      const info = await fetchLoaSummary(name);
      const mapped = mapClassToJobId(info.className);
      setJobId(mapped);
      setJobLabel(info.className || mapped);
      setEngraving(info.engraving || "");
      setCombatPower(info.combatPowerText ?? null);
      setLevel(info.itemAvgLevelText ?? null);

      if (!mapped || !info.engraving) {
        setErr("직업/직업각인을 확인할 수 없습니다. 닉네임을 다시 확인하세요.");
      }
    } catch (e) {
      console.error(e);
      setErr("닉네임 확인에 실패했습니다.");
      setJobId(""); setJobLabel(""); setEngraving(""); setCombatPower(null); setLevel(null);
    } finally {
      setChecking(false);
    }
  }

  async function handleJoin(e) {
    e.preventDefault();
    if (!valid || submitting) return;
    setErr(""); setSubmitting(true);

    try {
      const fn = typeof ensureJoinParty === "function" ? ensureJoinParty : joinParty;
      const ret = await fn({
        code: partyCode,
        uid: authUser.uid,
        nickname: nickname.trim(),
        job: jobId,
        engraving,
        combatPower, // 백엔드가 무시해도 무방
        level
      });

      const code = typeof ret === "string" ? partyCode : (ret?.code || partyCode);
      const already = (typeof ret === "object" && (ret.already || ret.alreadyJoined || ret.status === "already")) || false;
      if (already) alert("이미 참여 중인 공격대입니다. 해당 공격대로 이동합니다.");
      onJoined?.(code);
    } catch (e2) {
      const msg = e2?.message || "";
      if (e2?.code === "ALREADY_IN_PARTY" || /(이미\s*참여|already)/i.test(msg)) {
        alert("이미 참여 중인 공격대입니다. 해당 공격대로 이동합니다.");
        onJoined?.(partyCode);
      } else if (/not[_\s-]*found|존재하지|없습니다/i.test(msg)) {
        setErr("공격대를 찾을 수 없습니다.");
      } else {
        setErr("공격대 참여 중 오류가 발생했습니다.");
        console.error(e2);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppModal
      open={true}
      onClose={onClose}
      title="공격대 참여"
      maxWidthClass="max-w-lg"
      initialFocusRef={codeRef}
      footer={
        <button
          type="submit"
          form="joinPartyForm"
          className="btn btn-primary w-full disabled:opacity-60"
          disabled={!valid || submitting}
        >
          {submitting ? "참여 중..." : "공격대 참여하기"}
        </button>
      }
    >
      <form id="joinPartyForm" onSubmit={handleJoin} className="flex flex-col gap-4">
        {/* 공격대 코드 */}
        <div>
          <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">
            공격대 코드 <span className="text-xs text-zinc-500">(알파벳/숫자 6자리)</span>
          </label>
          <input
            ref={codeRef}
            className={inputCls + " tracking-widest uppercase w-full"}
            value={partyCode}
            onChange={handleCodeChange}
            placeholder="예: K7M3QZ"
            minLength={6}
            maxLength={6}
            inputMode="latin"
            autoComplete="one-time-code"
            required
          />
        </div>

        {/* 닉네임 + 확인 버튼 */}
        <div>
          <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">닉네임</label>
          <div className="flex gap-2">
            <input
              className={inputCls+ " flex-1"}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={12}
              placeholder="닉네임"
              required
            />
            <button
              type="button"
              onClick={checkNickname}
              className="btn btn-secondary whitespace-nowrap"
              disabled={checking || !nickname.trim()}
              title="로아 Open API로 직업/직각 조회"
            >
              {checking ? "확인중..." : "닉네임 확인"}
            </button>
          </div>
          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{nickname.length}/12</div>
        </div>

        {/* 직업/직업각인 (읽기 전용) + 전투력 hidden */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">직업</label>
            <input className={inputCls+ " w-full"} value={jobLabel} readOnly placeholder="닉네임 확인 후 자동입력" />
          </div>
          <div className="flex-1">
            <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">직업각인</label>
            <input className={inputCls+ " w-full"} value={engraving} readOnly placeholder="닉네임 확인 후 자동입력" />
          </div>
          <input type="hidden" name="combatPower" value={combatPower ?? ""} />
          <input type="hidden" name="level" value={level ?? ""} />
        </div>

        {!!err && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 dark:bg-red-900/20 dark:border-red-900/30 dark:text-red-300">
            {err}
          </div>
        )}
      </form>
    </AppModal>
  );
}
